/* eslint-disable no-param-reassign */
import * as Sentry from '@sentry/browser';
import _ from 'lodash';

import {
  date as commonDate,
  error as commonError,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Deliverable, Util } from '@finley-technologies/finley-types';

import { DeliverableProgressStatusGQLType } from 'types/dashboard-core-gqlTypes.generated.d';

export const getDeliverablesUrl = (pathname: string) => {
  const match = pathname.match(
    /(.*\/deliverables\/v3)(?=[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12})?|(.*\/deliverables)(?=\/[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12})?/,
  );
  if (!match) {
    return '';
  }
  return _.find(match, (m) => m && m.includes('deliverables'));
};

export const getDeliverablesQueryParams = (
  search: string,
): {
  startDate?: number;
  endDate?: number;
  statuses?: Deliverable.v3DEPRECATED.ProgressStatus[];
  creditFacilityIds?: string[];
} => {
  const queryParams: {
    startDate?: number;
    endDate?: number;
    statuses?: Deliverable.v3DEPRECATED.ProgressStatus[];
    creditFacilityIds?: string[];
  } = {};

  if (!search) {
    return queryParams;
  }
  const isQueryParameters = search.match(/(?:\?)(.*)/);
  if (!isQueryParameters) {
    Sentry.captureException(new Error('Query parameters malformed'));
  }

  const splitParams = search.slice(1).split('&');

  return _.reduce(
    splitParams,
    (acc, param) => {
      const [key, value] = param.split('=');
      if (key === 'statuses') {
        const statuses = value.replace(/\[|\]/g, '').split(',');
        if (
          !_.every(statuses, (status) =>
            _.includes(
              _.values(Deliverable.v3DEPRECATED.ProgressStatuses),
              status,
            ),
          )
        ) {
          Sentry.captureException(
            new commonError.FinleyError(
              'Invalid status provided as query parameter',
              {
                metadata: { logMeta: { search, statuses } },
              },
            ),
          );
          return acc;
        }
        acc.statuses = statuses as Deliverable.v3DEPRECATED.ProgressStatus[];
        return acc;
      }
      if (key === 'creditFacilityIds') {
        const creditFacilityIds = value.replace(/\[|\]/g, '').split(',');
        if (
          // @jendevelops TODO consolidate all UUID regexes
          !_.every(creditFacilityIds, (creditFacilityId) =>
            /[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}/.test(
              creditFacilityId,
            ),
          )
        ) {
          Sentry.captureException(
            new commonError.FinleyError(
              'Invalid credit facility id provided as query parameter',
              {
                metadata: { logMeta: { search, creditFacilityIds } },
              },
            ),
          );
          return acc;
        }
        acc.creditFacilityIds = creditFacilityIds;
        return acc;
      }
      if (key === 'startDate') {
        if (_.isNaN(new Date(Number(value)).valueOf())) {
          Sentry.captureException(
            new commonError.FinleyError(
              'Invalid start date provided as query parameter',
              {
                metadata: { logMeta: { search, startDate: value[0] } },
              },
            ),
          );
          return acc;
        }
        acc.startDate = Number(value);
      }
      if (key === 'endDate') {
        if (_.isNaN(new Date(Number(value)).valueOf())) {
          Sentry.captureException(
            new commonError.FinleyError(
              'Invalid end date provided as query parameter',
              {
                metadata: { logMeta: { search, endDate: value[0] } },
              },
            ),
          );
          return acc;
        }
        acc.endDate = Number(value);
      }
      return acc;
    },
    queryParams,
  );
};

/** Status Checkers */
export const isCompletedDeliverableStatus = (
  status: DeliverableProgressStatusGQLType,
) => _.includes(['submitted', 'waived'], status);

export const isDueDateOverdue = (dueDate: Util.FullDateStr) => {
  const today = commonDate.currentDateStr();
  return dueDate < today;
};

export const getStatusLabel = (
  status: DeliverableProgressStatusGQLType,
  dueDate: Util.FullDateStr,
) =>
  isDueDateOverdue(dueDate) && status === 'notSubmitted'
    ? 'Overdue'
    : commonUtils.toSentenceCase(status);

export type ComputedStatus =
  | 'submitted'
  | 'resubmit'
  | 'waived'
  | 'overdue'
  | 'upcoming';
export const getComputedStatus = (
  status: DeliverableProgressStatusGQLType,
  dueDate: Util.FullDateStr,
): ComputedStatus => {
  if (status === 'notSubmitted') {
    return isDueDateOverdue(dueDate) ? 'overdue' : 'upcoming';
  }

  return status;
};
